import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate, useParams, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { AISuggestion } from '@ds/ai';
import { Avatar, Badge, Button, Card, Divider, Icon, IconButton, Progress } from '@ds/core';
import { DocumentCard, Timeline } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { Menu, Tabs } from '@ds/navigation';
import { DataTable } from '@ds/data';
import { EnviarMensagem } from '@/comuns/EnviarMensagem';
import { dataEHoraPorExtenso, dataSimples, idadeEm, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { CargaDeDocumentos, CargaDoPaciente, FichaDoPaciente, FichaPreenchida, Profissional } from '@/data/tipos';
import { useRotaAtual } from '@/router/useRotaAtual';
import { rotaPorId } from '@/router/rotas';
import { NovoAgendamentoModal } from './NovoAgendamentoModal';
import { NovoDocumentoModal } from './documentos/NovoDocumentoModal';
import { PreencherFichaModal } from './paciente/FichaClinica';
import { ResumoDasConsultas } from './paciente/ResumoDasConsultas';
import { LinhaDoTempoDoProntuario } from './paciente/LinhaDoTempoDoProntuario';
import { AlterarRespostasModal } from './paciente/AlterarRespostasModal';
import { ImpressaoDaFichaModal } from './paciente/ImpressaoDaFichaModal';
import { pode } from '@/session/sessao';
import { CampoDaFicha } from './paciente/CampoDaFicha';
import { camposPeloCep } from './paciente/enderecoPeloCep';
import { consultarCep } from '@/comuns/cep';
import { GRUPOS_DA_FICHA } from './paciente/campos';
import { NovoOrcamentoModal } from './vendas/NovoOrcamentoModal';
import { OrcamentoAbertoModal } from './vendas/OrcamentoAbertoModal';
import { diaIso } from '@/comuns/formato';
import { estiloDaEtiqueta } from '@/comuns/contraste';
import { Etiquetas } from '@/comuns/Etiquetas';
import type { Etiqueta } from '@/data/cadastros';

/**
 * A ficha mais o cadastro de etiquetas da clínica (0168): a ficha traz o que ESTA pessoa
 * tem, e o cadastro, o que ela pode ganhar — as duas listas no mesmo carregamento, para o
 * menu "Etiquetar" não piscar vazio ao abrir.
 */
export interface CargaDaTelaDoPaciente extends CargaDoPaciente {
    etiquetasDaClinica: Etiqueta[];
}

export async function carregarPaciente({ params }: LoaderFunctionArgs): Promise<CargaDaTelaDoPaciente> {
    const [carga, cadastros] = await Promise.all([
        dados().paciente(params.contatoId ?? ''),
        dados().atendimentoECrm(),
    ]);
    return { ...carga, etiquetasDaClinica: cadastros.etiquetas };
}

const SEXO: Record<string, string> = {
    feminino: 'Feminino', masculino: 'Masculino', outro: 'Outro', 'nao-informado': 'Sexo não informado',
};

export function Paciente() {
    const carga = useLoaderData() as CargaDaTelaDoPaciente;
    const { contatoId = '' } = useParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const handle = useRotaAtual();
    const [confirmar, confirmacao] = useConfirmacao();

    const [gruposAbertos, setGruposAbertos] = useState<string[]>([]);
    const [campoEmEdicao, setCampoEmEdicao] = useState<string | null>(null);
    const [agendarAberto, setAgendarAberto] = useState(false);
    const [fichaAberta, setFichaAberta] = useState(false);
    const [orcamentoAberto, setOrcamentoAberto] = useState(false);
    const [emitindo, setEmitindo] = useState(false);
    const [cadastroDeDocumentos, setCadastroDeDocumentos] = useState<CargaDeDocumentos | null>(null);
    const [equipe, setEquipe] = useState<Profissional[]>([]);
    const [erroDaEquipe, setErroDaEquipe] = useState<string | null>(null);
    const [fichaEmCorrecao, setFichaEmCorrecao] = useState<FichaPreenchida | null>(null);
    /* A folha impressa mora na URL: o link leva direto à ficha certa. */
    const [buscaDaUrl, setBuscaDaUrl] = useSearchParams();
    const podeCorrigir = pode('atendimentos');
    const trocarNaUrl = (chave: 'ficha' | 'orcamento', id: string | null) => setBuscaDaUrl((atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (id) proximos.set(chave, id);
        else proximos.delete(chave);
        return proximos;
    });
    const trocarFichaNaUrl = (id: string | null) => trocarNaUrl('ficha', id);

    const { ficha, convenios, financeiro, linhaDoTempo, documentos, pacotes, orcamentos, titulos, prontuario } = carga;
    const aba = handle?.subrota?.path ?? 'informacoes';
    const rota = rotaPorId('paciente');
    const plano = convenios.find((c) => c.id === ficha.convenioId);

    /* A equipe alimenta o campo de profissional responsável, que era uma lista
       fixa de dois nomes. Sem ela o campo fica sem opções — e diz isso ao abrir. */
    useEffect(() => {
        let vivo = true;
        dados().opcoesDoAgendamento()
            .then((o) => { if (vivo) setEquipe(o.profissionais); })
            .catch((e: unknown) => { if (vivo) setErroDaEquipe(e instanceof Error ? e.message : 'Não foi possível ler a equipe.'); });
        return () => { vivo = false; };
    }, []);

    const irParaAba = (id: string) => navigate(`/app/contatos/${contatoId}/${id}`);

    const salvarCampo = async (campo: keyof FichaDoPaciente, valor: string, rotulo: string) => {
        try {
            await dados().atualizarCampoDaFicha(contatoId, campo, valor as FichaDoPaciente[typeof campo]);
            setCampoEmEdicao(null);
            revalidator.revalidate();
            aviso({ title: 'Campo atualizado', description: rotulo });
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
            return;
        }
        if (campo === 'cep') await completarPeloCep(valor);
    };

    /* O CEP salvo preenche rua, bairro, cidade e estado que estiverem VAZIOS — o
       mesmo ViaCEP da unidade. Falha do ViaCEP não desfaz o CEP: só avisa. */
    const completarPeloCep = async (valor: string) => {
        const resultado = await consultarCep(valor);
        if (!resultado.ok) {
            aviso({ title: 'Endereço não preenchido pelo CEP', description: resultado.motivo, tone: 'warning' });
            return;
        }
        const aPreencher = camposPeloCep(ficha, resultado.endereco);
        if (aPreencher.length === 0) return;
        try {
            for (const { campo, valor: doCep } of aPreencher) {
                await dados().atualizarCampoDaFicha(contatoId, campo, doCep);
            }
            revalidator.revalidate();
            aviso({ title: 'Endereço completado pelo CEP', description: aPreencher.map((c) => c.valor).join(' · ') });
        } catch (e) {
            aviso({ title: 'O CEP não completou o endereço', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    /* "Criar documento" dizia "Ação ainda não portada". A emissão existe: abre
       aqui, já com este paciente escolhido. O cadastro de modelos vem na hora. */
    const abrirEmissao = async () => {
        try {
            const cadastro = cadastroDeDocumentos ?? await dados().documentos({ aba: 'modelos', porPagina: 1 });
            setCadastroDeDocumentos(cadastro);
            setEmitindo(true);
        } catch (e) {
            aviso({ title: 'Não foi possível abrir a emissão', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const arquivar = async () => {
        const ok = await confirmar({
            titulo: `Arquivar ${ficha.nome}?`,
            descricao: 'O paciente sai da lista e da busca. Prontuário, documentos e financeiro continuam guardados.',
            confirmar: 'Arquivar paciente',
        });
        if (!ok) return;
        try {
            await dados().arquivarContato(contatoId);
            aviso({ title: 'Paciente arquivado', description: ficha.nome, tone: 'warning' });
            navigate('/app/contatos/pacientes');
        } catch (e) {
            aviso({ title: 'Não foi possível arquivar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const pacotesAtivos = pacotes.filter((p) => p.status === 'ativo');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <Avatar name={ficha.nome} size="xl" />
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h1 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 22, lineHeight: '30px', fontWeight: 600, letterSpacing: '-0.02em' }}>{ficha.nome}</h1>
                        <Badge tone="brand">Paciente</Badge>
                        {/* O convênio fica no cabeçalho porque quem atende precisa
                            saber o plano antes de propor procedimento, não depois. */}
                        {plano && (
                            <span
                                title={plano.tipo}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px',
                                    borderRadius: 'var(--radius-pill)', ...estiloDaEtiqueta(plano.cor),
                                    fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                                }}
                            >
                                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: plano.cor }} />
                                {plano.nome}
                            </span>
                        )}
                    </div>
                    <p style={{ margin: '5px 0 0', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                        {[SEXO[ficha.sexo], idadeEm(ficha.nascimento), ficha.telefone, ficha.cpf].filter(Boolean).join(' · ')}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-tertiary)' }}>
                        Paciente desde {dataSimples(ficha.cadastradoEm)} · {ficha.profissional || 'sem profissional responsável'}
                    </p>
                    {/* Etiqueta é o que a clínica marcou nesta pessoa — fica junto da
                        identificação porque é lida no mesmo relance que o nome. */}
                    <div style={{ marginTop: 8 }}>
                        <Etiquetas
                            rotulo={`Etiquetas de ${ficha.nome}`}
                            aplicadas={carga.etiquetas}
                            disponiveis={carga.etiquetasDaClinica}
                            aoAplicar={(id) => dados().etiquetarContato(contatoId, id)}
                            aoTirar={(id) => dados().desetiquetarContato(contatoId, id)}
                            onMudou={() => revalidator.revalidate()}
                        />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {/* Era um modal que dizia "mensagem enviada" e não enviava
                        nada. Agora escolhe o canal e cai na conversa de verdade. */}
                    <EnviarMensagem contatoId={contatoId} nome={ficha.nome} tamanho="md" />
                    <Button iconLeft={<Icon name="calendar-plus" size={15} />} onClick={() => setAgendarAberto(true)}>Agendar</Button>
                    <Menu
                        align="right" width={215}
                        onSelect={(id) => {
                            if (id === 'voltar') { navigate('/app/contatos/pacientes'); return; }
                            if (id === 'editar') {
                                irParaAba('informacoes');
                                setGruposAbertos(['identificacao']);
                                return;
                            }
                            if (id === 'doc') { void abrirEmissao(); return; }
                            if (id === 'orcamento') { setOrcamentoAberto(true); return; }
                            if (id === 'arquivar') void arquivar();
                        }}
                        trigger={<IconButton icon={<Icon name="ellipsis" size={17} />} label="Mais ações" variant="outline" size="lg" />}
                        items={[
                            { id: 'editar', label: 'Editar cadastro', icon: 'pencil' },
                            { id: 'doc', label: 'Criar documento', icon: 'file-plus' },
                            { id: 'orcamento', label: 'Criar orçamento', icon: 'receipt' },
                            { id: 'divisor', label: '', divider: true },
                            { id: 'voltar', label: 'Voltar para contatos', icon: 'arrow-left' },
                            { id: 'arquivar', label: 'Arquivar paciente', icon: 'archive', tone: 'danger' },
                        ]}
                    />
                </div>
            </div>

            <Tabs
                ariaLabel="Seções do paciente" activeId={aba} onChange={irParaAba}
                tabs={(rota?.children ?? []).map((c) => ({
                    id: c.path,
                    label: c.label,
                    count: c.path === 'documentos' ? documentos.length : undefined,
                }))}
            />

            <div style={{ display: 'grid', gridTemplateColumns: 'var(--layout-colunas, minmax(0,1.5fr) minmax(0,1fr))', gap: 16, alignItems: 'start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {aba === 'informacoes' && erroDaEquipe && (
                        <Alert tone="warning" title="A equipe não carregou" dismissible={false}>
                            O profissional responsável não pode ser trocado agora: {erroDaEquipe}
                        </Alert>
                    )}
                    {aba === 'informacoes' && GRUPOS_DA_FICHA.map((grupo) => {
                        const aberto = gruposAbertos.includes(grupo.id);
                        const preenchidos = grupo.campos.filter((c) => String(ficha[c.chave] ?? '') !== '').length;
                        return (
                            <div key={grupo.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
                                <h2 style={{ margin: 0 }}>
                                    <button
                                        type="button" aria-expanded={aberto} aria-controls={`grupo-${grupo.id}`}
                                        onClick={() => setGruposAbertos((l) => (aberto ? l.filter((x) => x !== grupo.id) : [...l, grupo.id]))}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '15px 18px',
                                            border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left',
                                        }}
                                    >
                                        {/* A seta gira: sem isso não se sabe se o bloco
                                            abriu ou se a tela trocou. */}
                                        <Icon
                                            name="chevron-down" size={17}
                                            style={{
                                                color: 'var(--neutral-500)', flex: '0 0 auto',
                                                transform: aberto ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                transition: 'transform var(--dur-base) var(--ease-standard)',
                                            }}
                                        />
                                        <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-ui)', fontSize: 15, fontWeight: 600, letterSpacing: 'var(--tracking-snug)', color: 'var(--text-primary)' }}>{grupo.titulo}</span>
                                        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                                            {preenchidos} de {grupo.campos.length}
                                        </span>
                                    </button>
                                </h2>

                                {aberto && (
                                    <div id={`grupo-${grupo.id}`} style={{ padding: '0 18px 14px' }}>
                                        {grupo.campos.map((campo, i) => (
                                            <CampoDaFicha
                                                key={campo.chave} campo={campo} ficha={ficha} convenios={convenios} equipe={equipe} primeiro={i === 0}
                                                editando={campoEmEdicao === campo.chave}
                                                onEditar={() => setCampoEmEdicao(campo.chave)}
                                                onCancelar={() => setCampoEmEdicao(null)}
                                                onSalvar={(valor) => salvarCampo(campo.chave, valor, campo.rotulo)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {aba === 'linha-do-tempo' && (
                        <Card title="Linha do tempo" subtitle="Todos os eventos do paciente" padding={18}>
                            {linhaDoTempo.length === 0 ? (
                                <EmptyState compact icon="history" title="Ainda não há eventos" description="Agendamentos, documentos e cobranças aparecem aqui." />
                            ) : (
                                <Timeline
                                    events={linhaDoTempo.map((e) => ({
                                        id: e.id, title: e.titulo, time: dataEHoraPorExtenso(e.quandoEm), author: e.autor,
                                        description: e.descricao, icon: e.icone, tone: e.tom,
                                        meta: e.detalhes?.map((d) => ({ label: d.rotulo, value: d.valor })),
                                    }))}
                                />
                            )}
                        </Card>
                    )}

                    {/* A carteira é o que o paciente já contratou e ainda tem para
                        usar. Era uma aba vazia dizendo "ainda não foi portada". */}
                    {aba === 'carteira' && (
                        <Card title="Carteira" subtitle="O que o paciente já contratou e ainda tem para usar" padding={18}>
                            {pacotesAtivos.length === 0 && financeiro.emAbertoCentavos === 0 ? (
                                <EmptyState compact icon="wallet" title="Nada em aberto" description="Sem pacote ativo e sem valor a receber deste paciente." />
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {pacotesAtivos.map((p) => {
                                        const restantes = Math.max(0, p.sessoesTotais - p.sessoesUsadas);
                                        return (
                                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                                                <span style={{ flex: 1, minWidth: 0 }}>
                                                    <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600 }}>{p.nome}</span>
                                                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                                        {p.sessoesUsadas} de {p.sessoesTotais} usadas{p.validade ? ` · vale até ${dataSimples(p.validade)}` : ''}
                                                    </span>
                                                </span>
                                                <Badge tone={restantes > 0 ? 'success' : 'neutral'} dot>
                                                    {restantes} {restantes === 1 ? 'sessão restante' : 'sessões restantes'}
                                                </Badge>
                                            </div>
                                        );
                                    })}
                                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 }}>
                                        <dt style={{ color: 'var(--text-secondary)' }}>A receber</dt>
                                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(financeiro.emAbertoCentavos)}</dd>
                                        <dt style={{ color: 'var(--text-secondary)' }}>Vencido</dt>
                                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: financeiro.vencidoCentavos ? 'var(--danger-strong)' : 'var(--text-primary)' }}>{moeda(financeiro.vencidoCentavos)}</dd>
                                    </dl>
                                </div>
                            )}
                            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                Crédito avulso — valor pago antes de escolher o procedimento — ainda não é registrado pelo VecchIA.
                            </p>
                        </Card>
                    )}

                    {aba === 'documentos' && (
                        <Card
                            title="Documentos" padding={18}
                            actions={<Button size="sm" onClick={() => void abrirEmissao()}>Novo documento</Button>}
                        >
                            {documentos.length === 0 ? (
                                <EmptyState compact icon="file-text" title="Nenhum documento" description="Termos, contratos e anamneses do paciente aparecem aqui." />
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {documentos.map((d) => (
                                        <DocumentCard
                                            key={d.id} title={d.titulo} patient={d.tipo} updatedAt={dataEHoraPorExtenso(d.atualizadoEm)}
                                            status={d.status} pages={d.paginas}
                                            /* Abre na tela de Documentos, com o documento aberto:
                                               é lá que se assina, recusa e imprime. */
                                            onClick={() => navigate(`/app/documentos?q=${encodeURIComponent(d.titulo)}&documento=${encodeURIComponent(d.id)}`)}
                                        />
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}

                    {aba === 'prontuario' && (
                        <Card
                            title="Prontuário" subtitle="Consultas com registro, da mais recente para a mais antiga" padding={18}
                            actions={<Button size="sm" onClick={() => setFichaAberta(true)}>Preencher ficha</Button>}
                        >
                            <LinhaDoTempoDoProntuario
                                contatoId={contatoId} prontuario={prontuario}
                                onImprimir={trocarFichaNaUrl}
                                {...(podeCorrigir ? { onAlterar: setFichaEmCorrecao } : {})}
                            />
                        </Card>
                    )}

                    {aba === 'prontuario' && buscaDaUrl.get('ficha') && (
                        <ImpressaoDaFichaModal
                            ficha={prontuario.fichas.find((f) => f.id === buscaDaUrl.get('ficha')) ?? null}
                            paciente={ficha.nome} nascimento={ficha.nascimento}
                            onFechar={() => trocarFichaNaUrl(null)}
                        />
                    )}
                    {fichaEmCorrecao && (
                        <AlterarRespostasModal
                            ficha={fichaEmCorrecao} contatoId={contatoId}
                            onFechar={() => setFichaEmCorrecao(null)} onAlterada={() => revalidator.revalidate()}
                        />
                    )}

                    {aba === 'formularios' && (
                        <Card
                            title="Formulários" subtitle="Modelos disponíveis na clínica" padding={18}
                            actions={<Button size="sm" onClick={() => setFichaAberta(true)}>Preencher</Button>}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {prontuario.modelos.map((m) => {
                                    const usos = prontuario.fichas.filter((f) => f.modeloId === m.id).length;
                                    return (
                                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                                            <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600 }}>{m.nome}</span>
                                            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{m.campos.length} campos</span>
                                            <Badge tone={usos ? 'success' : 'neutral'} dot>{usos ? `${usos} preenchida${usos > 1 ? 's' : ''}` : 'nunca usada'}</Badge>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* O construtor de modelos é outra tela, e ela existe: o texto
                                dizia que "entra com o construtor", como se faltasse. */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                <span>Criar e editar modelos fica no construtor de fichas.</span>
                                <Button variant="link" onClick={() => navigate('/app/atendimentos/fichas')}>Abrir o construtor de fichas</Button>
                            </div>
                        </Card>
                    )}

                    {aba === 'pacotes' && (
                        <Card title="Pacotes" subtitle="Sessões contratadas e o que já foi usado" padding={18}>
                            {pacotes.length === 0 ? (
                                <EmptyState compact icon="layers" title="Nenhum pacote" description="Pacotes nascem da aprovação de um orçamento." />
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {pacotes.map((p) => (
                                        <div key={p.id}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600 }}>{p.nome}</span>
                                                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{moeda(p.valorCentavos)}</span>
                                                <Badge tone={p.status === 'ativo' ? 'success' : 'neutral'} dot>{p.status === 'ativo' ? 'Ativo' : 'Encerrado'}</Badge>
                                            </div>
                                            <Progress
                                                label={`${p.sessoesUsadas} de ${p.sessoesTotais} sessões`}
                                                value={Math.round((p.sessoesUsadas / p.sessoesTotais) * 100)} showValue
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}

                    {aba === 'orcamentos' && (
                        <Card
                            title="Orçamentos" padding={18}
                            actions={<Button size="sm" onClick={() => setOrcamentoAberto(true)}>Novo orçamento</Button>}
                        >
                            {orcamentos.length === 0 ? (
                                <EmptyState compact icon="receipt" title="Nenhum orçamento" description="Orçamentos criados em Vendas aparecem aqui." />
                            ) : (
                                <DataTable
                                    rowKey="id" rows={orcamentos}
                                    onRowClick={(o) => trocarNaUrl('orcamento', o.id)}
                                    rowLabel={(o) => `${o.numero} · ${o.resumo}`}
                                    columns={[
                                        { key: 'numero', header: 'Número' },
                                        { key: 'resumo', header: 'Itens' },
                                        { key: 'criadoEm', header: 'Criado', render: (o) => dataSimples(o.criadoEm) },
                                        { key: 'status', header: 'Situação', render: (o) => <Badge tone={o.status === 'aprovado' ? 'success' : o.status === 'recusado' ? 'danger' : 'neutral'} dot>{o.status}</Badge> },
                                        { key: 'totalCentavos', header: 'Valor', align: 'right', render: (o) => moeda(o.totalCentavos) },
                                    ]}
                                />
                            )}
                        </Card>
                    )}

                    {aba === 'orcamentos' && buscaDaUrl.get('orcamento') && (
                        <OrcamentoAbertoModal
                            key={buscaDaUrl.get('orcamento')}
                            orcamentoId={buscaDaUrl.get('orcamento') ?? ''}
                            onFechar={() => trocarNaUrl('orcamento', null)}
                            onMudou={() => revalidator.revalidate()}
                        />
                    )}

                    {aba === 'financeiro' && (
                        <Card title="Financeiro do paciente" subtitle="Títulos ligados a este cadastro" padding={18}>
                            {titulos.length === 0 ? (
                                <EmptyState compact icon="circle-dollar-sign" title="Nenhum título" description="Cobranças aparecem aqui assim que forem lançadas." />
                            ) : (
                                <DataTable
                                    rowKey="id" rows={titulos}
                                    columns={[
                                        { key: 'descricao', header: 'Descrição' },
                                        { key: 'vencimento', header: 'Vencimento', render: (t) => dataSimples(t.vencimento) },
                                        { key: 'situacao', header: 'Situação', render: (t) => <Badge tone={t.situacao === 'pago' ? 'success' : 'warning'} dot>{t.situacao === 'pago' ? 'Quitado' : t.situacao === 'parcial' ? 'Parcial' : 'Em aberto'}</Badge> },
                                        {
                                            key: 'valorCentavos', header: 'Valor', align: 'right',
                                            render: (t) => (
                                                <span>
                                                    {moeda(t.valorCentavos)}
                                                    {t.pagoCentavos > 0 && t.situacao !== 'pago' && (
                                                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>falta {moeda(t.valorCentavos - t.pagoCentavos)}</span>
                                                    )}
                                                </span>
                                            ),
                                        },
                                    ]}
                                />
                            )}
                        </Card>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* SEMPRE visível, acima do Resumo financeiro (decisão do usuário em
                        22/09/2026, revendo o pedido inicial de mostrá-lo só no Prontuário):
                        quem abre a ficha quer entender o caso em qualquer aba. */}
                    <ResumoDasConsultas contatoId={contatoId} />

                    <Card title="Resumo financeiro" padding={18}>
                        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '9px 16px', fontSize: 13 }}>
                            {([
                                ['Total investido', moeda(financeiro.totalInvestidoCentavos), 'var(--text-primary)'],
                                ['Em aberto', financeiro.emAbertoCentavos ? moeda(financeiro.emAbertoCentavos) : '—', 'var(--warning-strong)'],
                                ['Vencido', financeiro.vencidoCentavos ? moeda(financeiro.vencidoCentavos) : '—', 'var(--danger-strong)'],
                                ['Ticket médio', moeda(financeiro.ticketMedioCentavos), 'var(--text-primary)'],
                            ] as const).map(([rotulo, valor, cor]) => (
                                <div key={rotulo} style={{ display: 'contents' }}>
                                    <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
                                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: valor === '—' ? 'var(--text-tertiary)' : cor }}>{valor}</dd>
                                </div>
                            ))}
                        </dl>
                        {financeiro.pacote && (
                            <>
                                <Divider spacing={14} />
                                <Progress
                                    label={`${financeiro.pacote.nome} — ${financeiro.pacote.sessoesConcluidas} de ${financeiro.pacote.sessoesTotais} concluídas`}
                                    value={Math.round((financeiro.pacote.sessoesConcluidas / financeiro.pacote.sessoesTotais) * 100)}
                                    showValue
                                />
                            </>
                        )}
                    </Card>

                    {financeiro.pacote && (
                        <AISuggestion
                            text={`Faltam ${financeiro.pacote.sessoesTotais - financeiro.pacote.sessoesConcluidas} sessões do ${financeiro.pacote.nome.toLowerCase()}. Agendar o retorno?`}
                            onAccept={() => setAgendarAberto(true)}
                        />
                    )}

                    {/* Só fora de Informações: lá as alergias já são campo editável,
                        e repetir o mesmo dado em dois lugares convida a divergirem. */}
                    {aba !== 'informacoes' && ficha.alergias && (
                        <Card
                            title="Alergias e observações" padding={18}
                            actions={<Button variant="link" onClick={() => { irParaAba('informacoes'); setGruposAbertos(['clinico']); setCampoEmEdicao('alergias'); }}>Editar</Button>}
                        >
                            <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-body)' }}>{ficha.alergias}</p>
                        </Card>
                    )}
                </div>
            </div>

            <NovoAgendamentoModal
                open={agendarAberto} onClose={() => setAgendarAberto(false)} dataPadrao={diaIso()}
                pacientePadrao={{ id: contatoId, nome: ficha.nome }}
                onCriado={() => revalidator.revalidate()}
            />

            <NovoOrcamentoModal
                open={orcamentoAberto} onClose={() => setOrcamentoAberto(false)}
                pacientePadrao={{ id: contatoId, nome: ficha.nome }}
                onCriado={() => revalidator.revalidate()}
            />

            {cadastroDeDocumentos && (
                <NovoDocumentoModal
                    aberto={emitindo} contatoInicial={contatoId}
                    modelos={cadastroDeDocumentos.modelos} pacientes={cadastroDeDocumentos.pacientes}
                    procedimentos={cadastroDeDocumentos.procedimentos} profissionais={cadastroDeDocumentos.profissionais}
                    onClose={() => setEmitindo(false)}
                    onEmitido={() => revalidator.revalidate()}
                />
            )}

            <PreencherFichaModal
                open={fichaAberta} contatoId={contatoId} prontuario={prontuario}
                onClose={() => setFichaAberta(false)} onSalva={() => revalidator.revalidate()}
            />

            {confirmacao}
        </div>
    );
}
